import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faCubes, faTag } from '@fortawesome/free-solid-svg-icons';
import {
    AccordionHeader,
    AccordionBody,
    CompensationBadge,
    SectionTitle,
    ServiceGroupCard,
    GroupHeader,
    TierTable,
    EmptyState
} from '../styles/BillingStyledComponents';
import { getRoundingModeLabel } from 'components/common/SunnyUtils';

/**
 * 청구 정보 카드 상단의 적용 요금 정책 접이식 표시.
 *
 * - 접힌 상태: "적용 요금 정책: {정책명} v{버전}" + 정책 보전 배지(있을 때) + chevron
 * - 펼친 상태: 정책 트리 전체 (정책 정보 + 그룹 + 항목 + 구간 가격 정의)
 *
 * 이건 "이번 달 사용량 분배"가 아니라 "정책 자체의 정의"를 보여주는 것이므로,
 * 빈 그룹(servicePriceList === [])도 그대로 표시한다 (정책에 정의되어 있다면).
 *
 * 시각 디자인은 SiteInfo.js 라인 935~1140의 인라인 정책 정보 카드를 참고하되,
 * BillingStyledComponents의 컴포넌트로 통일.
 */
const PolicyTreeAccordion = ({
    policyTree,
    pricingPolicyName,
    pricingPolicyVersion,
    defaultExpanded = false
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    if (!pricingPolicyName) return null;

    const policyComp = parseFloat(policyTree?.compensationAmount || 0);

    return (
        <div style={{ marginBottom: '1.5rem' }}>
            <SectionTitle>적용 요금 정책</SectionTitle>
            <AccordionHeader
                type="button"
                $expanded={expanded}
                onClick={() => setExpanded(prev => !prev)}
            >
                <span className="accordion-main">
                    <FontAwesomeIcon icon={faTag} style={{ color: '#1e40af', fontSize: '0.95rem' }} />
                    <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                        {pricingPolicyName}
                        {pricingPolicyVersion != null && (
                            <span style={{ marginLeft: '8px', color: '#475569', fontWeight: 600 }}>
                                v{pricingPolicyVersion}
                            </span>
                        )}
                    </span>
                    {policyComp > 0 && (
                        <CompensationBadge style={{ marginLeft: '4px' }}>
                            <span className="comp-label">정책 보전</span>
                            <span className="comp-value">₩{policyComp.toLocaleString()}</span>
                        </CompensationBadge>
                    )}
                </span>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`accordion-icon ${expanded ? 'expanded' : ''}`}
                />
            </AccordionHeader>

            {expanded && (
                <AccordionBody>
                    {policyTree ? (
                        <PolicyTreeBody policyTree={policyTree} />
                    ) : (
                        <EmptyState>정책 트리 정보를 불러올 수 없습니다.</EmptyState>
                    )}
                </AccordionBody>
            )}
        </div>
    );
};

/**
 * 정책 트리의 본문 — 정책 정보 + 그룹/항목/구간 트리 표시.
 */
const PolicyTreeBody = ({ policyTree }) => {
    const description = policyTree.description;
    const groups = policyTree.serviceGroupList || [];

    return (
        <>
            {/* 정책 설명 (있을 때만) — 정책명과 보전금액은 헤더에 이미 있어 제거 */}
            {description && (
                <div style={{ marginBottom: '1.5rem' }}>
                    <SectionTitle style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>설명</SectionTitle>
                    <p style={{ color: '#475569', fontSize: '0.9rem', margin: 0, lineHeight: 1.6 }}>
                        {description}
                    </p>
                </div>
            )}

            {/* 서비스 구성 */}
            <SectionTitle style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                <FontAwesomeIcon icon={faCubes} style={{ marginRight: '6px', color: '#3b82f6' }} />
                서비스 구성
            </SectionTitle>
            {groups.length > 0 ? (
                groups.map((group, idx) => (
                    <PolicyGroupBlock key={group.uuid || idx} group={group} />
                ))
            ) : (
                <EmptyState>구성된 서비스가 없습니다.</EmptyState>
            )}
        </>
    );
};

/**
 * 정책 트리의 그룹 1개 — 그룹명 + 그룹 보전금액 + servicePriceList.
 */
const PolicyGroupBlock = ({ group }) => {
    const groupName = group.serviceItemName || '그룹';
    const groupComp = parseFloat(group.compensationAmount || 0);
    const items = group.servicePriceList || [];

    return (
        <ServiceGroupCard style={{ marginBottom: '0.75rem' }}>
            <GroupHeader as="div" style={{ cursor: 'default' }}>
                <span className="group-label">
                    <FontAwesomeIcon icon={faCubes} style={{ color: '#3b82f6', fontSize: '0.85rem' }} />
                    {groupName}
                </span>
                {groupComp > 0 && (
                    <span className="group-amount">
                        <CompensationBadge>
                            <span className="comp-label">그룹 보전</span>
                            <span className="comp-value">₩{groupComp.toLocaleString()}</span>
                        </CompensationBadge>
                    </span>
                )}
            </GroupHeader>

            {items.length === 0 ? (
                <EmptyState>등록된 서비스 항목이 없습니다.</EmptyState>
            ) : (
                items.map((item, idx) => (
                    <PolicyItemBlock key={item.uuid || idx} item={item} />
                ))
            )}
        </ServiceGroupCard>
    );
};

/**
 * 정책 트리의 항목 1개 — 항목명 + 단가 + 항목 보전 + 구간 가격 정의 표.
 */
const PolicyItemBlock = ({ item }) => {
    const itemName = item.serviceItemName || '항목';
    const itemComp = parseFloat(item.compensationAmount || 0);
    const tiers = item.serviceTierPriceList || [];
    const paymentUnit = item.paymentUnit || '';

    return (
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px dashed #e2e8f0' }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.75rem',
                gap: '12px',
                flexWrap: 'wrap'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6' }} />
                    <span style={{ fontWeight: 700, color: '#334155', fontSize: '0.95rem' }}>{itemName}</span>
                    {itemComp > 0 && (
                        <CompensationBadge>
                            <span className="comp-label">항목 보전</span>
                            <span className="comp-value">₩{itemComp.toLocaleString()}</span>
                        </CompensationBadge>
                    )}
                    <span
                        title="측정된 사용량의 소수점 처리 방식"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '3px 10px',
                            background: '#dbeafe',
                            border: '1px solid #93c5fd',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            color: '#1e40af'
                        }}
                    >
                        <span style={{ fontWeight: 600, opacity: 0.8 }}>최종 수량 반영 방법</span>
                        <span style={{ fontWeight: 700 }}>{getRoundingModeLabel(item.roundingMode)}</span>
                    </span>
                </div>
                <span style={{ color: '#475569', fontWeight: 600, fontSize: '0.9rem' }}>
                    ₩{(item.price || 0).toLocaleString()}
                    {paymentUnit && <span style={{ color: '#94a3b8', marginLeft: '4px' }}>/ {paymentUnit}</span>}
                </span>
            </div>

            {tiers.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <TierTable>
                        <thead>
                            <tr>
                                <th>시작 수량</th>
                                <th>종료 수량</th>
                                <th>할인</th>
                                <th style={{ textAlign: 'right' }}>적용 가격</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tiers.map((tier, tIdx) => {
                                const isPercent = tier.discountUnit === 'percent';
                                // 백엔드에서 계산된 할인 적용 단가 사용 (PricingPolicyService.enrichTierAppliedPrice)
                                const appliedPrice = tier.appliedPrice != null ? tier.appliedPrice : item.price;
                                return (
                                    <tr key={tIdx}>
                                        <td>{tier.startAmount}</td>
                                        <td>{tier.endAmount}</td>
                                        <td>
                                            {tier.discountAmount}{isPercent ? '%' : '원'}
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: 600, color: '#1e293b' }}>
                                            ₩{appliedPrice.toLocaleString()}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </TierTable>
                </div>
            )}
        </div>
    );
};

export default PolicyTreeAccordion;
